import React from 'react';
import {
  AssetLink,
  ContentWrapper,
  FeatureAnimationWrapper,
  FeatureBackgroundImage,
  RegistryFeatureBackgroundImage,
  FeatureForegroundImage,
  RegistryFeatureForegroundImage,
  FeatureItem,
  FeatureItemWrapper,
  FeaturesColumn,
  FeaturesList,
  FeatureUiImage,
  RegistryFeatureUiImage,
  LinkDescription,
  LinkTitle,
  ChevronWrapper,
  PhotoCreditContent,
  rightBlockWidthRatio,
  StyledColumns,
  StyledSection,
  topBorderHeight,
  InvitationsFeatureUiImageContainer,
} from '@sections/home/ProductFeaturesSectionV2/ProductFeaturesSectionV2.styles';
import { useProductFeaturesSectionController } from '@sections/home/ProductFeaturesSectionV2/ProductFeaturesSectionV2.controller';
import { Box } from '@components/Box';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { JoykitIcon } from '@components/JoykitIcon';
import { DexecureImageOptions } from '@utils/cloudAssetUrl.util';
import { useTranslation } from 'react-i18next';
import { Flex } from '@components/Flex';

const featureBgAspectRatio = 3000 / 2000;
const registryBgAspectRatio = 3000 / 2250;
const weddingWebsiteBgAspectRatio = 1500 / 1531;
const guestListUiAspectRatio = 3980 / 2106;
const saveTheDateUiAspectRatio = 125 / 99;
const saveTheDateUiAspectRatioString = '125 / 99';
const invitesUiAspectRatio = 125 / 99;
const invitesFgAspectRatioString = '125 / 99';
const smartRSVPUiAspectRatio = 3000 / 2000;
const registryUiAspectRatio = 3000 / 2019;
const mobileAppUiAspectRatio = 439 / 938;
const registryFgAspectRatio = 3000 / 1171;
const hotelUiAspectRatio = 3000 / 2500;
const hotelBgAspectRatio = 3600 / 2287;
const hotelFgAspectRatio = 3600 / 2625;

const LinksMapping = [
  '/wedding-website',
  '/registry',
  '/hotel-room-blocks',
  '/guest-list',
  '/cards/wedding/save-the-date',
  '/wedding-invitations',
  '/online-rsvp',
  '/app',
];

const FeatureAnimationContainer = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<{ isFirst?: boolean; isForHover?: boolean; selectedFeatureIndex: number }>
>(({ isFirst, isForHover, children, selectedFeatureIndex }, ref) => {
  return (
    <FeatureAnimationWrapper ref={ref} isFirst={isFirst} isForHover={isForHover}>
      <div>{children}</div>
      <AssetLink
        id={`linkAsset${selectedFeatureIndex}`}
        aria-label={LinksMapping[selectedFeatureIndex]}
        href={LinksMapping[selectedFeatureIndex]}
      />
    </FeatureAnimationWrapper>
  );
});

export const ProductFeaturesSectionV2: React.FC = () => {
  const {
    sectionRef,
    weddingWebsiteFeatureWrapperRef,
    guestListFeatureWrapperRef,
    saveTheDatesFeatureWrapperRef,
    invitationsFeatureWrapperRef,
    smartRSVPFeatureWrapperRef,
    registryFeatureWrapperRef,
    mobileAppFeatureWrapperRef,
    hotelRoomblocksFeatureWrapperRef,
    leftSideMenuContainerRef,
    selectedFeatureIndex,
    isAppearedInViewport,
    setIsAppearedInViewport,
    resizeWidth,
  } = useProductFeaturesSectionController();

  const { t } = useTranslation('home');

  const featureItems = [
    {
      id: 'home-features-to-wedding-website',
      to: '/wedding-website',
      label: t('sections.features.weddingWebsite.listItemLabel'),
      desc: t('sections.features.weddingWebsite.desc'),
    },
    {
      id: 'home-features-to-registry',
      to: '/registry',
      label: t('sections.features.registry.listItemLabel'),
      desc: t('sections.features.registry.desc'),
    },
    {
      id: 'home-features-to-hotel-room-blocks',
      to: '/hotel-room-blocks',
      label: t('sections.features.hotelRoomBlocks.listItemLabel'),
      desc: t('sections.features.hotelRoomBlocks.desc'),
    },
    {
      id: 'home-features-to-guest-list',
      to: '/guest-list',
      label: t('sections.features.guestList.listItemLabel'),
      desc: t('sections.features.guestList.desc'),
    },
    {
      id: 'home-features-save-the-dates',
      to: '/cards/wedding/save-the-date',
      label: t('sections.features.saveTheDates.listItemLabel'),
      desc: t('sections.features.saveTheDates.desc'),
    },
    {
      id: 'home-features-to-invitations',
      to: '/wedding-invitations',
      label: t('sections.features.invitations.listItemLabel'),
      desc: t('sections.features.invitations.desc'),
    },
    {
      id: 'home-features-to-smart-rsvp',
      to: '/online-rsvp',
      label: t('sections.features.smartRSVP.listItemLabel'),
      desc: t('sections.features.smartRSVP.desc'),
    },
    {
      id: 'home-features-to-guest-app',
      to: '/app',
      label: t('sections.features.guestApp.listItemLabel'),
      desc: t('sections.features.guestApp.desc'),
    },
  ];
  const featureImageProps: DexecureImageOptions & { isMainSrcShown: boolean } = {
    resizeWidth,
    isMainSrcShown: isAppearedInViewport,
    optimization: 'default',
  };

  return (
    <StyledSection ref={sectionRef} as="section" backgroundColor={'yellow1'} id="Routers">
      <ContentWrapper>
        <Box
          style={{
            right: 0,
            width: `calc(${rightBlockWidthRatio * 100}%)`,
            height: '100%',
            position: 'absolute',
            overflow: 'hidden',
            zIndex: 2,
          }}
        >
          <LazyLoadWrapper beforeLoad={() => setIsAppearedInViewport(true)}>
            <div />
          </LazyLoadWrapper>
          <FeatureAnimationContainer selectedFeatureIndex={selectedFeatureIndex} ref={weddingWebsiteFeatureWrapperRef} isFirst={true}>
            <Box style={{ background: '#DADAC9', height: '100%', width: '100%' }} />
            <FeatureUiImage
              src={'home/router-sections/router-wedding-website/ww-1.png'}
              alt={t('sections.features.weddingWebsite.uiAlt')}
              {...featureImageProps}
              aspectratio={weddingWebsiteBgAspectRatio}
              scale={1.15}
              style={{ width: '100%', top: 0, left: '50%' }}
            />
            <FeatureUiImage
              src={'home/router-sections/router-wedding-website/ww-2.png'}
              alt={t('sections.features.weddingWebsite.uiAlt')}
              {...featureImageProps}
              aspectratio={weddingWebsiteBgAspectRatio}
              scale={1.15}
              style={{ width: '100%', top: 0, left: '50%' }}
            />
          </FeatureAnimationContainer>
          <FeatureAnimationContainer selectedFeatureIndex={selectedFeatureIndex} ref={registryFeatureWrapperRef}>
            <RegistryFeatureBackgroundImage
              src={'home/router-sections/router-registry/registry-background.png'}
              alt=""
              {...featureImageProps}
              aspectratio={registryBgAspectRatio}
              style={{ transformOrigin: 'center top' }}
              isEagerLoading={true}
              scale={1.07}
            />
            <RegistryFeatureUiImage
              src={t('sections.features.registry.foregroundSrc')}
              alt={t('sections.features.registry.uiAlt')}
              {...featureImageProps}
              scale={0.1}
              aspectratio={registryUiAspectRatio}
              style={{ width: '100%', height: 'auto', opacity: 1, transformOrigin: 'center bottom' }}
            />
            <RegistryFeatureForegroundImage
              src={'home/router-sections/router-registry/registry-couple.png'}
              alt={t('sections.features.registry.foregroundAlt')}
              {...featureImageProps}
              scale={1.3}
              aspectratio={registryFgAspectRatio}
            />
          </FeatureAnimationContainer>
          <FeatureAnimationContainer selectedFeatureIndex={selectedFeatureIndex} ref={hotelRoomblocksFeatureWrapperRef}>
            <FeatureBackgroundImage
              src={'home/router-sections/router-travel/bedroom-v2.webp'}
              alt={t('sections.features.hotelRoomBlocks.backgroundAlt')}
              {...featureImageProps}
              aspectratio={hotelBgAspectRatio}
              isEagerLoading={true}
              scale={1}
              style={{ top: '51%', left: '50%' }}
            />
            <FeatureUiImage
              src={'home/router-sections/router-travel/hotels.webp'}
              alt={t('sections.features.hotelRoomBlocks.uiAlt')}
              {...featureImageProps}
              aspectratio={hotelUiAspectRatio}
              scale={1.63}
              style={{ width: '100%', height: 'auto', opacity: 0.9, top: '14%', left: '50%' }}
            />
            <FeatureForegroundImage
              src={'home/router-sections/router-travel/couple.webp'}
              alt={t('sections.features.hotelRoomBlocks.foregroundAlt')}
              {...featureImageProps}
              aspectratio={hotelFgAspectRatio}
              style={{ transform: 'translate(-50%, 0%)', top: '0%' }}
            />
          </FeatureAnimationContainer>
          <FeatureAnimationContainer selectedFeatureIndex={selectedFeatureIndex} ref={guestListFeatureWrapperRef}>
            <FeatureBackgroundImage
              src={'home/router-sections/router-guest-list/router-gl-bg.jpg'}
              alt=""
              {...featureImageProps}
              aspectratio={featureBgAspectRatio}
              isEagerLoading={true}
            />
            <FeatureUiImage
              src={'home/router-sections/router-guest-list/router-gl-ui-bg.png'}
              alt={t('sections.features.guestList.uiAlt')}
              {...featureImageProps}
              aspectratio={guestListUiAspectRatio}
              style={{ opacity: 1, transformOrigin: 'center 70%' }}
            />
            <FeatureUiImage
              src={t('sections.features.guestList.foregroundSrc')}
              alt={t('sections.features.guestList.uiAlt')}
              {...featureImageProps}
              aspectratio={guestListUiAspectRatio}
              style={{ opacity: 1, transformOrigin: 'center 70%' }}
            />
            <FeatureForegroundImage
              src={'home/router-sections/router-guest-list/router-gl-couple.png'}
              alt={t('sections.features.guestList.foregroundAlt')}
              {...featureImageProps}
              aspectratio={featureBgAspectRatio}
            />
          </FeatureAnimationContainer>
          <FeatureAnimationContainer selectedFeatureIndex={selectedFeatureIndex} ref={saveTheDatesFeatureWrapperRef}>
            <FeatureBackgroundImage
              src={'home/router-sections/router-save-the-dates/router-std-bg-v2.png'}
              alt=""
              {...featureImageProps}
              scale={1}
              aspectratio={saveTheDateUiAspectRatio}
              isEagerLoading={true}
            />
            <InvitationsFeatureUiImageContainer
              style={{ aspectRatio: saveTheDateUiAspectRatioString }}
              aspectratio={saveTheDateUiAspectRatio}
            >
              <FeatureUiImage
                src={'home/router-sections/router-save-the-dates/router-std-cards-v2.png'}
                alt={t('sections.features.saveTheDates.uiAlt')}
                {...featureImageProps}
                aspectratio={saveTheDateUiAspectRatio}
                style={{
                  width: '100%',
                  aspectRatio: saveTheDateUiAspectRatioString,
                  objectFit: 'contain',
                  top: 0,
                }}
              />
              <FeatureUiImage
                src={'home/router-sections/router-save-the-dates/router-std-envelope-back.png'}
                alt={t('sections.features.saveTheDates.envelopeBackAlt')}
                {...featureImageProps}
                aspectratio={1}
                width="40%"
                style={{ top: '10%' }}
              />
              <FeatureUiImage
                src={'home/router-sections/router-save-the-dates/router-std-main-card.png'}
                alt={t('sections.features.saveTheDates.mainCardAlt')}
                {...featureImageProps}
                aspectratio={1}
                width="30%"
                style={{ top: 0 }}
              />
              <FeatureUiImage
                src={'home/router-sections/router-save-the-dates/router-std-envelope-front.png'}
                alt={t('sections.features.saveTheDates.envelopeFrontAlt')}
                {...featureImageProps}
                aspectratio={1}
                width="40%"
                style={{ top: '10%' }}
              />
            </InvitationsFeatureUiImageContainer>
            <FeatureForegroundImage
              src={'home/router-sections/router-save-the-dates/router-std-shadow.png'}
              alt={t('sections.features.saveTheDates.foregroundAlt')}
              {...featureImageProps}
              aspectratio={saveTheDateUiAspectRatio}
              style={{
                aspectRatio: saveTheDateUiAspectRatioString,
              }}
            />
          </FeatureAnimationContainer>
          <FeatureAnimationContainer selectedFeatureIndex={selectedFeatureIndex} ref={invitationsFeatureWrapperRef}>
            <InvitationsFeatureUiImageContainer style={{ aspectRatio: invitesFgAspectRatioString }} aspectratio={invitesUiAspectRatio}>
              <FeatureUiImage
                src={'home/router-sections/router-invites/invites-couple-v4.png'}
                alt={t('sections.features.invitations.firstCoupleAlt')}
                {...featureImageProps}
                aspectratio={invitesUiAspectRatio}
                style={{
                  left: '24%',
                  width: '33%',
                  bottom: '14%',
                  objectFit: 'cover',
                  minWidth: 'unset',
                  minHeight: 'unset',
                  top: 'unset',
                  objectPosition: 'center center',
                  transform: 'none',
                  opacity: 1,
                }}
              />
              <FeatureUiImage
                src={'home/router-sections/router-invites/invites-couple-2-v4.png'}
                alt={t('sections.features.invitations.secondCoupleAlt')}
                {...featureImageProps}
                aspectratio={invitesUiAspectRatio}
                style={{
                  right: '0%',
                  width: '26%',
                  bottom: 'unset',
                  left: 'unset',
                  objectFit: 'cover',
                  minWidth: 'unset',
                  minHeight: 'unset',
                  top: '12%',
                  objectPosition: 'center center',
                  transform: 'none',
                  opacity: 1,
                }}
              />
            </InvitationsFeatureUiImageContainer>
            <FeatureBackgroundImage
              src={'home/router-sections/router-invites/invites-background-v4.png'}
              alt={t('sections.features.invitations.uiAlt')}
              {...featureImageProps}
              aspectratio={invitesUiAspectRatio}
              isEagerLoading={true}
            />
            <InvitationsFeatureUiImageContainer style={{ aspectRatio: invitesFgAspectRatioString }} aspectratio={invitesUiAspectRatio}>
              <FeatureUiImage
                src={'home/router-sections/router-invites/invites-cards-v5.png'}
                alt={t('sections.features.invitations.uiAlt')}
                {...featureImageProps}
                aspectratio={invitesUiAspectRatio}
                style={{
                  width: '100%',
                  left: '50%',
                }}
              />
            </InvitationsFeatureUiImageContainer>
            <FeatureForegroundImage
              src={'home/router-sections/router-invites/invites-shadow.png'}
              alt={t('sections.features.invitations.foregroundAlt')}
              {...featureImageProps}
              aspectratio={invitesUiAspectRatio}
              style={{
                aspectRatio: invitesFgAspectRatioString,
              }}
            />
          </FeatureAnimationContainer>
          <FeatureAnimationContainer selectedFeatureIndex={selectedFeatureIndex} ref={smartRSVPFeatureWrapperRef}>
            <FeatureBackgroundImage
              src={'home/router-sections/router-inv-rsvp/router-inv-rsvp-bg.jpg'}
              alt=""
              {...featureImageProps}
              scale={1.2}
              aspectratio={featureBgAspectRatio}
              isEagerLoading={true}
            />
            <FeatureUiImage
              src={'home/router-sections/router-inv-rsvp/router-inv-rsvp-card.jpg'}
              alt={t('sections.features.smartRSVP.uiAlt')}
              scale={0.25}
              {...featureImageProps}
              aspectratio={smartRSVPUiAspectRatio}
              style={{ width: '100%', bottom: '2%', top: 'unset', left: '50%', opacity: 0.5, transformOrigin: 'center bottom' }}
            />
            <FeatureUiImage
              src={'home/router-sections/router-inv-rsvp/router-inv-rsvp-ui-bubbles.png'}
              alt={t('sections.features.smartRSVP.uiAlt')}
              scale={0.75}
              {...featureImageProps}
              aspectratio={smartRSVPUiAspectRatio}
              style={{ width: '100%', top: '10%', transformOrigin: 'center top' }}
            />
            <FeatureForegroundImage
              src={'home/router-sections/router-inv-rsvp/router-inv-rsvp-couple.png'}
              alt={t('sections.features.smartRSVP.foregroundAlt')}
              {...featureImageProps}
              aspectratio={featureBgAspectRatio}
              style={{ bottom: 0, top: 'unset', transform: 'translate(-50%,0) scale(0.35)' }}
            />
          </FeatureAnimationContainer>
          <FeatureAnimationContainer selectedFeatureIndex={selectedFeatureIndex} ref={mobileAppFeatureWrapperRef}>
            <FeatureBackgroundImage
              src={'home/router-sections/router-mobile/router-mobile-bg.jpg'}
              alt=""
              {...featureImageProps}
              aspectratio={featureBgAspectRatio}
              isEagerLoading={true}
            />
            <FeatureForegroundImage
              src={'home/router-sections/router-mobile/router-mobile-couple.png'}
              alt={t('sections.features.guestApp.foregroundAlt')}
              {...featureImageProps}
              aspectratio={mobileAppUiAspectRatio}
              style={{
                top: '16%',
                width: '100%',
                transformOrigin: 'center 35%',
                transform: 'translate(-50%, 0)',
              }}
            />
            <FeatureUiImage
              src={t('sections.features.guestApp.foregroundSrc')}
              alt=""
              {...featureImageProps}
              aspectratio={mobileAppUiAspectRatio}
              style={{
                width: '100%',
                opacity: 0,
                transformOrigin: 'center 35%',
                top: '17%',
                transform: 'translate(-50%, 0)',
              }}
            />
          </FeatureAnimationContainer>
        </Box>
        <StyledColumns verticalAlign={'center'}>
          <FeaturesColumn width={5}>
            <FeaturesList isControlVersion={true} ref={leftSideMenuContainerRef}>
              {featureItems.map((featureItem, index) => {
                const isActive = selectedFeatureIndex === index;
                return (
                  <FeatureItem key={featureItem.id} id={featureItem.id} href={featureItem.to} $isActive={isActive}>
                    <FeatureItemWrapper>
                      <Flex alignItems="center">
                        <LinkTitle>{featureItem.label} &nbsp;</LinkTitle>
                        <ChevronWrapper>
                          <JoykitIcon name="forwardChevron" color="linkHomeFeatureHover" />
                        </ChevronWrapper>
                      </Flex>
                      {isActive && (
                        <LinkDescription variant="featureCopy16" color="mono12" tagName="span" textAlign="left">
                          {featureItem.desc}
                        </LinkDescription>
                      )}
                    </FeatureItemWrapper>
                  </FeatureItem>
                );
              })}
            </FeaturesList>
          </FeaturesColumn>
        </StyledColumns>
        <PhotoCreditContent color="linkHomeFeature" id="photoCreditHomeProductFeature" />
      </ContentWrapper>
    </StyledSection>
  );
};
